import gql from 'graphql-tag';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useLazyQuery, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { useAdyen, useLogger, useI18n, useDispatch } from '~Hooks';
import NumberInput from '~ComponentsImproved/Numpad';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import {
  GetAdyenPayments,
  GetAdyenPayments_operativeViewer_adyenPayments,
  GetAdyenPaymentsVariables,
} from './__queries__/GetAdyenPayments';
import { addToast } from '~actions';

// GraphQL query to fetch Adyen payments for a specific time range
const TRANSACTIONS_QUERY = gql`
  query GetAdyenPayments($createdAt_gt: String, $createdAt_lt: String) {
    operativeViewer {
      id
      adyenPayments(createdAt_gt: $createdAt_gt, createdAt_lt: $createdAt_lt) {
        id
        createdAt
        pspReference
        amount
        methodIdentifier
        refundedAmount
        status
        origin
      }
    }
  }
`;

// Styled components for the RefundList UI
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  z-index: 2;
  overflow: hidden;
`;

const Header = styled(Box)`
  background: var(--colorBlueGray1300);
  border-bottom: 1px solid var(--colorBlueGray1200);
  flex-shrink: 0;
`;

const DateNav = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: center;
`;

const NavButton = styled.button`
  background: var(--colorBlueGray1200);
  color: var(--colorWhite);
  border: none;
  padding: 8px 12px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  min-width: 70px;
  touch-action: manipulation;

  &:active {
    background: var(--colorBlueGray1100);
  }
`;

const CloseButton = styled.button`
  background: #ae2929ff;
  color: var(--colorWhite);
  border: none;
  padding: 6px 16px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  touch-action: manipulation;

  &:active {
    background: #8e2121ff;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

const PaymentCard = styled.div`
  background: var(--colorBlueGray1300);
  border: 1px solid var(--colorBlueGray1200);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
  touch-action: manipulation;

  &:active {
    background: var(--colorBlueGray1200);
    transform: scale(0.98);
  }
`;

const PaymentRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  background: ${(props) => {
    switch (props.status) {
      case 'COMPLETED':
        return '#2d5f2e';
      case 'REFUNDED':
        return '#ae2929ff';
      case 'PENDING':
        return '#6b5b00';
      default:
        return 'var(--colorBlueGray1100)';
    }
  }};
  color: var(--colorWhite);
`;

const LoadingText = styled(Paragraph)`
  text-align: center;
  padding: 24px;
`;

const EmptyText = styled(Paragraph)`
  text-align: center;
  padding: 24px;
`;

interface RefundListProps {
  onClose?: () => void;
}

/**
 * RefundList component displays all Adyen payments for a selected day
 * and allows staff to refund individual payments.
 *
 * Day boundaries are set to 05:30 - 05:30 to align with typical restaurant
 * closing times, rather than midnight to midnight.
 */
const RefundList: React.FC<RefundListProps> = ({ onClose }) => {
  const logger = useLogger('refund-list');
  const adyen = useAdyen();
  const { i18n } = useI18n();
  const dispatch = useDispatch();

  // State for managing selected date (defaults to today)
  // Using Luxon DateTime for proper date handling
  const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());

  // State for managing refund amount input
  const [showRefundInput, setShowRefundInput] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

  // Calculate date range for the selected day (05:30 to 05:30)
  // Using Luxon for precise date/time manipulation
  const dateRange = useMemo(() => {
    // Start of business day at 05:30
    const startOfDay = selectedDate.set({ hour: 5, minute: 30, second: 0, millisecond: 0 });

    // End of business day at 05:30 the next day
    const endOfDay = startOfDay.plus({ days: 1 });

    logger.info('Date range calculated', {
      start: startOfDay.toISO(),
      end: endOfDay.toISO(),
    });

    return {
      start: startOfDay.toISO(),
      end: endOfDay.toISO(),
    };
  }, [selectedDate]);

  // Lazy query to fetch payments for the selected date range
  const paymentsQuery = useQuery<GetAdyenPayments, GetAdyenPaymentsVariables>(TRANSACTIONS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      createdAt_gt: dateRange.start!,
      createdAt_lt: dateRange.end!,
    },
  });

  const selectedPayment = paymentsQuery.data?.operativeViewer?.adyenPayments.find(
    (p) => p.id === selectedPaymentId,
  );

  // Fetch payments whenever the date range changes

  // Navigate to previous day using Luxon
  const handlePreviousDay = () => {
    const newDate = selectedDate.minus({ days: 1 });
    setSelectedDate(newDate);
    logger.info('Navigated to previous day', { date: newDate.toISO() });
  };

  // Navigate to next day using Luxon
  const handleNextDay = () => {
    const newDate = selectedDate.plus({ days: 1 });
    setSelectedDate(newDate);
    logger.info('Navigated to next day', { date: newDate.toISO() });
  };

  // Navigate to today using Luxon
  const handleToday = () => {
    setSelectedDate(DateTime.now());
    logger.info('Navigated to today');
  };

  // Handle payment card tap - show refund input
  const handlePaymentTap = (payment: GetAdyenPayments_operativeViewer_adyenPayments) => {
    logger.info('Payment selected for refund', {
      pspReference: payment.pspReference,
    });

    if (payment.refundedAmount >= payment.amount) {
      logger.info('Payment already fully refunded', {
        pspReference: payment.pspReference,
      });
      return;
    }

    setSelectedPaymentId(payment.id);
    setShowRefundInput(true);
  };

  // Process the refund with the specified amount
  const handleRefundSubmit = async (amountString: string) => {
    if (!selectedPayment) return;

    if (selectedPayment.refundedAmount >= selectedPayment.amount) {
      logger.info('Payment already fully refunded', {
        pspReference: selectedPayment.pspReference,
      });
      setSelectedPaymentId(null);
      setShowRefundInput(false);
      return;
    }

    const refundAmount = Math.round(parseFloat(amountString) * 100); // Convert to cents

    logger.info('Refund initiated', {
      pspReference: selectedPayment.pspReference,
      originalAmount: selectedPayment.amount,
      refundAmount,
    });

    setShowRefundInput(false);

    try {
      // Call Adyen refund API
      const result = await adyen.refundPayment({
        transactionId: selectedPayment.pspReference,
        createdAt: selectedPayment.createdAt,
        amount: refundAmount,
      });

      if (result.success) {
        logger.info('Refund successful', {
          pspReference: selectedPayment.pspReference,
          refundAmount,
        });

        // Show success toast
        dispatch(addToast(i18n('TOAST.Refund.Successful'), true, null, false));

        // Refresh the payment list
        paymentsQuery.refetch();
      } else {
        logger.error('Refund failed', {
          pspReference: selectedPayment.pspReference,
          mess: result.message,
        });

        // Show failure toast
        dispatch(addToast(i18n('TOAST.Refund.Failed'), true, null, true));
      }
    } catch (error) {
      logger.error('Refund error', {
        pspReference: selectedPayment.pspReference,
        err: (error as Error).message,
      });

      // Show failure toast on exception
      dispatch(addToast(i18n('TOAST.Refund.Failed'), true, null, true));
    } finally {
      setSelectedPaymentId(null);
    }
  };

  // Format currency for display
  const formatCurrency = (amountInCents: number) => {
    return `${(amountInCents / 100).toFixed(2)} kr`;
  };

  // Format date for display using Luxon
  const formatDate = (isoString: string) => {
    const date = DateTime.fromISO(isoString);
    return date.toFormat('yyyy-MM-dd HH:mm');
  };

  // Format the selected date for the header using Luxon
  const formatHeaderDate = (date: DateTime) => {
    const today = DateTime.now();
    const isToday = date.hasSame(today, 'day');

    if (isToday) {
      return 'Today';
    }

    // Format as "weekday, day month year" in Swedish locale
    return date.setLocale('sv-SE').toFormat('cccc, d MMMM yyyy');
  };

  const payments = paymentsQuery.data?.operativeViewer?.adyenPayments || [];
  const loading = paymentsQuery.loading;

  return (
    <Overlay>
      {/* Header with date navigation */}
      <Header padding="medium">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="small">
          <Heading level={3} color="var(--colorWhite)">
            Payment Refunds
          </Heading>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </Box>

        <DateNav>
          <NavButton onClick={handlePreviousDay}>← Prev</NavButton>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Heading level={4} color="var(--colorWhite)">
              {formatHeaderDate(selectedDate)}
            </Heading>
          </Box>
          <NavButton onClick={handleNextDay}>Next →</NavButton>
        </DateNav>

        <Box marginTop="small">
          <NavButton onClick={handleToday} style={{ width: '100%' }}>
            Today
          </NavButton>
        </Box>
      </Header>

      {/* Payment list */}
      <Content>
        {loading && (
          <LoadingText level={2} color="var(--colorWhite)">
            Loading payments...
          </LoadingText>
        )}

        {!loading && payments.length === 0 && (
          <EmptyText level={2} color="var(--colorBlueGray200)">
            No payments found for this day
          </EmptyText>
        )}

        {!loading &&
          payments.map((payment) => (
            <PaymentCard key={payment.id} onClick={() => handlePaymentTap(payment)}>
              <PaymentRow marginBottom="xs">
                <Heading level={4} color="var(--colorWhite)">
                  {formatCurrency(payment.amount)}
                </Heading>
                <StatusBadge status={payment.status}>{payment.status}</StatusBadge>
              </PaymentRow>

              {payment.refundedAmount > 0 && (
                <PaymentRow marginBottom="xs">
                  <Paragraph level={3} color="var(--colorBlueGray200)">
                    Refunded:
                  </Paragraph>
                  <Paragraph level={3} color="var(--colorWhite)">
                    {formatCurrency(payment.refundedAmount)}
                  </Paragraph>
                </PaymentRow>
              )}

              <PaymentRow marginBottom="xs">
                <Paragraph level={3} color="var(--colorBlueGray200)">
                  Time:
                </Paragraph>
                <Paragraph level={3} color="var(--colorWhite)">
                  {formatDate(payment.createdAt)}
                </Paragraph>
              </PaymentRow>

              <PaymentRow marginBottom="xs">
                <Paragraph level={3} color="var(--colorBlueGray200)">
                  Last four digits:
                </Paragraph>
                <Paragraph
                  level={3}
                  color="var(--colorWhite)"
                  style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {payment.methodIdentifier}
                </Paragraph>
              </PaymentRow>
            </PaymentCard>
          ))}
      </Content>

      {/* Refund amount input modal */}
      {showRefundInput && selectedPayment && (
        <NumberInput
          onClose={() => {
            setShowRefundInput(false);
            setSelectedPaymentId(null);
          }}
          onSubmit={handleRefundSubmit}
          title={`Refund Amount (Max: ${formatCurrency(
            selectedPayment.amount - selectedPayment.refundedAmount,
          )})`}
          maxValue={(selectedPayment.amount - selectedPayment.refundedAmount) / 100}
          submitText="Refund"
          decimal
        />
      )}
    </Overlay>
  );
};

export default RefundList;
